import { MAX_OPTION_VALUE_CHILDREN } from '@sec-loans-ui/ifp-loans/steps/household-info/contants'
import { IfpBasicInfoFormData } from '@sec-loans-ui/ifp-loans/steps/ifp-info/types'
import { IfpWizardData } from '@sec-loans-ui/ifp-loans/wizard/types'
import { assertValue } from '@shared/helpers'
import {
  IfpApplicationRequestData,
  IfpApplicationState,
  IfpGetApplicationResponse,
  IfpLoan,
  InstalmentFreePeriodMonthsDurationRange,
} from '@shared/sec-loans'
import { OTHER_VALUE } from '@ui-common/constants'
import { PHONE_COUNTRY_CODE, YesNo } from '@ui-common/types'
import {
  bffDateStringToFormDateString,
  getYesNoDefault,
  numberToString,
  resolveBooleanInputToYesNo,
  toNumber,
} from '@ui-common/utils'

import { getAndValidateApplicantsInfoData, isApplicantInfoValid } from './steps/applicant-info/applicantInfoStepUtils'
import {
  getAndValidateHouseholdInfoData,
  isHouseholdInfoValid as validateHouseholdInfo,
} from './steps/household-info/householdInfoStepUtils'
import { IfpHouseholdInfoFormData } from './steps/household-info/types'
import { getAndValidateIfpInfoData, isIfpInfoValid } from './steps/ifp-info/ifpInfoStepUtils'
import { IfpWizardType, WizardContext, WizardStep } from './wizard/wizardMachine'

export const wizardDataToApplicationData = (
  wizardData: Partial<IfpWizardData>,
  loan: IfpLoan,
): Partial<IfpApplicationRequestData> => {
  return {
    basicInfo: getAndValidateIfpInfoData(wizardData),
    householdInfo: getAndValidateHouseholdInfoData(wizardData, loan),
    applicantInfos: getAndValidateApplicantsInfoData(wizardData, loan),
  }
}

export const getWizardData = (wizardData: IfpWizardData, loan: IfpLoan): IfpWizardData => {
  const { basicInfo } = wizardData
  const wizardType = getWizardFlow({
    durationInMonths: toNumber(basicInfo?.durationInMonths),
    hasFinancialDifficulties: basicInfo?.hasFinancialDifficulties,
    principalExemptionMonthsLeft: loan.principalExemptionMonthsLeft,
  })
  return {
    ...wizardData,
    householdInfo: wizardType === IfpWizardType.Long ? wizardData?.householdInfo : undefined,
    applicantInfos: wizardData?.applicantInfos?.map((applicantInfo) => ({
      uuid: applicantInfo.uuid,
      phoneNumber: applicantInfo.phoneNumber,
      ...(wizardType === IfpWizardType.Long ? applicantInfo : undefined),
    })),
  }
}

export const getIsWizardDataComplete = ({
  data: { basicInfo, householdInfo, applicantInfos },
  loan,
}: {
  data: IfpWizardData
  loan: IfpLoan
}): boolean => {
  const isShortFlow = isShortWizardFlow({
    principalExemptionMonthsLeft: loan.principalExemptionMonthsLeft,
    durationInMonths: toNumber(basicInfo?.durationInMonths),
    hasFinancialDifficulties: basicInfo?.hasFinancialDifficulties,
  })
  const debtorsCount = loan.debtors.length
  const isBasicInfoValid = isIfpInfoValid(basicInfo)
  const isHouseholdInfoValid = isShortFlow || validateHouseholdInfo(householdInfo)
  const isApplicantInfosValid =
    debtorsCount === applicantInfos?.length &&
    applicantInfos.every((applicantInfo) => isApplicantInfoValid(applicantInfo, isShortFlow))

  return isBasicInfoValid && isHouseholdInfoValid && isApplicantInfosValid
}

type WizardTypeFunctionParams = {
  principalExemptionMonthsLeft: number | undefined
  durationInMonths: number | undefined
  hasFinancialDifficulties: YesNo | undefined
}

export const isLongWizardFlow = ({
  principalExemptionMonthsLeft,
  durationInMonths,
  hasFinancialDifficulties,
}: WizardTypeFunctionParams): boolean => {
  return (
    getWizardFlow({
      principalExemptionMonthsLeft,
      durationInMonths,
      hasFinancialDifficulties,
    }) === IfpWizardType.Long
  )
}

export const isShortWizardFlow = ({
  principalExemptionMonthsLeft,
  durationInMonths,
  hasFinancialDifficulties,
}: WizardTypeFunctionParams): boolean => {
  return (
    getWizardFlow({
      principalExemptionMonthsLeft,
      durationInMonths,
      hasFinancialDifficulties,
    }) === IfpWizardType.Short
  )
}

export const getWizardFlow = ({
  principalExemptionMonthsLeft,
  durationInMonths,
  hasFinancialDifficulties,
}: WizardTypeFunctionParams): IfpWizardType => {
  if (
    !!principalExemptionMonthsLeft &&
    !!durationInMonths &&
    durationInMonths <= principalExemptionMonthsLeft &&
    hasFinancialDifficulties === YesNo.No
  ) {
    return IfpWizardType.Short
  }
  return IfpWizardType.Long
}

export const omitFinnishPhoneCountryCode = (phoneNumber?: string): string | undefined => {
  return phoneNumber?.replace(PHONE_COUNTRY_CODE, '')
}

export const applicationDataToWizardData = (
  applicationData: Partial<IfpApplicationRequestData>,
): Partial<IfpWizardData> => {
  const { basicInfo, householdInfo, applicantInfos } = applicationData
  return {
    basicInfo: basicInfo && {
      durationInMonths: basicInfo.durationInMonths.toString() as IfpBasicInfoFormData['durationInMonths'],
      durationInMonthsRange:
        basicInfo.durationInMonths <= 6
          ? InstalmentFreePeriodMonthsDurationRange.OneToSix
          : InstalmentFreePeriodMonthsDurationRange.SevenToTwelve,
      startsImmediately: assertValue(
        resolveBooleanInputToYesNo(basicInfo.startsImmediately),
        'basicInfo.startsImmediately',
      ),
      startDate: bffDateStringToFormDateString(basicInfo.startDate),
      primaryReason: basicInfo.primaryReason,
      primaryReasonOther: basicInfo.primaryReasonOther,
      hasFinancialDifficulties: assertValue(
        resolveBooleanInputToYesNo(basicInfo.hasFinancialDifficulties),
        'basicInfo.hasFinancialDifficulties',
      ),
    },
    householdInfo: householdInfo && resolveApplicationHouseholdInfo(householdInfo),
    applicantInfos: applicantInfos?.map((applicantInfo) => ({
      ...applicantInfo,
      isPayingChildSupport: getYesNoDefault(!!applicantInfo, applicantInfo.childSupports?.length),
      grossSalary: numberToString(applicantInfo.grossSalary),
      netSalary: numberToString(applicantInfo.netSalary),
      normalGrossSalary: numberToString(applicantInfo.normalGrossSalary),
      normalNetSalary: numberToString(applicantInfo.normalNetSalary),
      incomeBeforeTaxes: numberToString(applicantInfo.incomeBeforeTaxes),
      incomeAfterTaxes: numberToString(applicantInfo.incomeAfterTaxes),
      currentIncomeBeforeTaxes: numberToString(applicantInfo.currentIncomeBeforeTaxes),
      currentIncomeAfterTaxes: numberToString(applicantInfo.currentIncomeAfterTaxes),
      hasOtherIncome: getYesNoDefault(!!applicantInfo, applicantInfo.otherIncomes?.length),
      otherIncomes: applicantInfo.otherIncomes?.map((otherIncome) => ({
        incomeType: otherIncome.incomeType,
        amountAfterTaxes: numberToString(otherIncome.amountAfterTaxes),
      })),
      hasJobToReturnTo: resolveBooleanInputToYesNo(applicantInfo.hasJobToReturnTo),
      expectsToBeEmployedAgain: resolveBooleanInputToYesNo(applicantInfo.expectsToBeEmployedAgain),
      totalSavings: numberToString(applicantInfo.totalSavings),
      ownShareOfHousingExpenses: numberToString(applicantInfo.ownShareOfHousingExpenses),
      childSupports: applicantInfo.childSupports?.map((childSupport) => ({
        ageGroup: childSupport.ageGroup,
        supportAmount: numberToString(childSupport.supportAmount),
      })),
      hasCarOwnership: getYesNoDefault(!!applicantInfo, !!applicantInfo.carOwnershipType),
      totalLoansAmount: numberToString(applicantInfo.totalLoansAmount),
      totalAmountOfLoanRepaymentsPerMonth: numberToString(applicantInfo.totalAmountOfLoanRepaymentsPerMonth),
      payingHousingCompanyLoan: getYesNoDefault(!!applicantInfo, !!applicantInfo.totalHousingCompanyLoan),
      totalHousingCompanyLoan: numberToString(applicantInfo.totalHousingCompanyLoan),
    })),
  }
}

const resolveApplicationHouseholdInfo = (
  householdInfo: Required<IfpApplicationRequestData>['householdInfo'],
): IfpHouseholdInfoFormData => {
  const numberOfChildren = householdInfo.numberOfChildren
  const hasMoreThatMaxOptionValue = numberOfChildren > MAX_OPTION_VALUE_CHILDREN

  return {
    numberOfAdults: householdInfo.numberOfAdults.toString(),
    numberOfChildren: hasMoreThatMaxOptionValue ? OTHER_VALUE : `${householdInfo.numberOfChildren}`,
    numberOfChildrenOther: hasMoreThatMaxOptionValue ? numberOfChildren.toString() : undefined,
  }
}

export const getOtherDebtors = (userDebtorId: string | undefined, loan: IfpLoan): IfpLoan['debtors'] =>
  loan.debtors.filter((d) => d.uuid !== userDebtorId)

export const getProgressInfo = ({
  step,
  debtorsCount,
  type,
  currentApplicantIndex,
}: {
  step: WizardStep
  debtorsCount: number
  type?: IfpWizardType
  currentApplicantIndex?: number
}): { stepIndex: number; totalSteps: number } => {
  const applicantIndex = currentApplicantIndex !== undefined ? currentApplicantIndex : 0

  const totalSteps = type === IfpWizardType.Short ? 1 + debtorsCount : 2 + debtorsCount

  if (type === IfpWizardType.Short) {
    let stepIndex = step === WizardStep.IfpInfoStep ? 1 : 2 + applicantIndex

    if (stepIndex > totalSteps) {
      stepIndex = totalSteps
    }

    return { stepIndex, totalSteps }
  }

  switch (step) {
    case WizardStep.IfpInfoStep:
      return { stepIndex: 1, totalSteps }
    case WizardStep.HouseholdInfoStep:
      return { stepIndex: 2, totalSteps }
    case WizardStep.ApplicantInfoStep:
      let stepIndex = 3 + applicantIndex

      if (stepIndex > totalSteps) {
        stepIndex = totalSteps
      }

      return { stepIndex, totalSteps }
    default:
      return { stepIndex: 1, totalSteps }
  }
}

export const shouldGoToEndStep = (
  { context }: { context: WizardContext },
  data: Pick<IfpGetApplicationResponse, 'actionsNeeded' | 'state'>,
): boolean => {
  const sendToEndStep: Record<IfpApplicationState, () => boolean> = {
    Sending: () => true,
    WaitingForResend: () => true,
    Pending: () => true,
    Accepted: () => true,
    Draft: () => false,
    ReturnedByCoDebtor: () => false,
    WaitingForCoDebtor: () => {
      const { user } = context
      const debtorUuid = assertValue(user.debtorUuid, 'user.debtorUuid')

      const actionsNeededByDebtorUuids = assertValue(data.actionsNeeded, 'data.actionsNeeded')
      const isActionRequiredFromUser = actionsNeededByDebtorUuids.includes(debtorUuid)

      return !isActionRequiredFromUser
    },

    // since guards cannot handle throwing,
    // pass invalid states to endStep in order to throw there
    UnavailableOther: () => true,
    UnavailableDraftInvalidated: () => true,
    Cancelled: () => true,
    Rejected: () => true,
  }

  return sendToEndStep[data.state]()
}
